import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  applyTheme,
  persistTheme,
  readStoredTheme,
  resolveInitialTheme,
  systemTheme,
  type Theme,
} from './theme';

export interface ThemeState {
  readonly theme: Theme;
  /** True when the current theme came from the OS rather than an explicit choice. */
  readonly isSystemDerived: boolean;
  readonly setTheme: (theme: Theme) => void;
  readonly toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

export function ThemeProvider({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  const [theme, setThemeState] = useState<Theme>(() => resolveInitialTheme());
  const [isSystemDerived, setIsSystemDerived] = useState<boolean>(() => readStoredTheme() === null);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    // Follow the OS only while the technician has not made a choice. Once they have, their
    // choice is theirs and an OS change must not overrule it.
    if (!isSystemDerived) {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = (): void => {
      setThemeState(systemTheme());
    };
    media.addEventListener('change', onChange);
    return () => {
      media.removeEventListener('change', onChange);
    };
  }, [isSystemDerived]);

  const setTheme = useCallback((next: Theme): void => {
    setThemeState(next);
    setIsSystemDerived(false);
    persistTheme(next);
  }, []);

  const value = useMemo<ThemeState>(
    () => ({
      theme,
      isSystemDerived,
      setTheme,
      toggleTheme: () => {
        setTheme(theme === 'dark' ? 'light' : 'dark');
      },
    }),
    [isSystemDerived, setTheme, theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const context = useContext(ThemeContext);
  if (context === null) {
    throw new Error('useTheme must be used inside ThemeProvider');
  }
  return context;
}
